<template>
  <div class="form-group">
    <label v-if="label" class="text-truncate mb-2 mt-1 w-100 d-flex gap-1">
      <span>{{ label }}</span>
      <span v-if="required" class="text-danger"> •</span>
      <Tippy v-if="note" :content="note" :disable="false" class="bx bx-info-circle bx-xs" style="margin-top: 1.5px;" as="i" :options="{ placement: 'top', arrow: true, offset: [0, 15]}"></Tippy>
    </label>
    <el-date-picker
      v-model="range"
      class="my-date-picker"
      type="daterange"
      format="dd/MM/yyyy"
      start-placeholder="Start date"
      end-placeholder="End date"
      :picker-options="{ shortcuts }"
      range-separator="-"
      @change="onRangeChange"
      clearable
      unlink-panels
      style="width: 100%;"
      :class="[ { dateInvalid: hasError, dateWarning: hasWarning } ]"
    />
    <!-- Error/Warning Message -->
    <div v-if="hasError && !hideMessage" class="mt-2 error-message">
      {{ (validation.$errors[0].$message == "Value is required" || validation.$errors[0].$message == "The value is required") && label ? `${label} is required` : validation.$errors[0].$message }}
    </div>
    <div v-if="hasWarning" class="mt-2 warning-message">
      {{ warningMessage }}
    </div>
  </div>

</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DatePicker } from 'element-ui'
import 'element-ui/lib/theme-chalk/date-picker.css'

export default {
  name: 'DateRangePickerPicker',
  components: { ElDatePicker: DatePicker },

  props: {
    start:       { type: String, default: null },
    end:         { type: String, default: null },
    default:     { type: String, default: '' },
    label:       { type: String, default: "Date Range"},
    required:    { type: Boolean, default: false},
    note:        { type: String },
    cacheId:     { type: String,  default: null  },
    cache:       { type: Boolean, default: false },
    validation: { type: Object, description: "Input Validation", },
    hasWarning: { type: Boolean, default: false },
    warningMessage: { type: String, default: "" },
    rememberToday: { type: Boolean, default: false },
  },

  data() {
    return {
      range: [],

      shortcuts: [
        {
          text: 'Today',
          onClick(picker) {
            const now = new Date()
            picker.$emit('pick', [now, now])
          }
        },
        {
          text: 'Yesterday',
          onClick(picker) {
            const d = new Date()
            d.setDate(d.getDate() - 1)
            picker.$emit('pick', [d, d])
          }
        },
        {
          text: 'Last 7 Days',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setDate(end.getDate() - 6)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: 'Last 30 Days',
          onClick(picker) {
            const end = new Date()
            const start = new Date()
            start.setDate(end.getDate() - 29)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: 'This Week',
          onClick(picker) {
            const today = new Date()
            const dow   = today.getDay() || 7
            const mon   = new Date(today)
            mon.setDate(today.getDate() - (dow - 1))
            const sun   = new Date(mon)
            sun.setDate(mon.getDate() + 6)
            picker.$emit('pick', [mon, sun])
          }
        },
        {
          text: 'Last Week',
          onClick(picker) {
            const today   = new Date()
            const dow     = today.getDay() || 7
            const thisMon = new Date(today)
            thisMon.setDate(today.getDate() - (dow - 1))
            const lastMon = new Date(thisMon)
            lastMon.setDate(thisMon.getDate() - 7)
            const lastSun = new Date(lastMon)
            lastSun.setDate(lastMon.getDate() + 6)
            picker.$emit('pick', [lastMon, lastSun])
          }
        },
        {
          text: 'This Month',
          onClick(picker) {
            const now   = new Date()
            const start = new Date(now.getFullYear(), now.getMonth(), 1)
            const end   = new Date(now.getFullYear(), now.getMonth()+1, 0)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: 'Last Month',
          onClick(picker) {
            const now   = new Date()
            const start = new Date(now.getFullYear(), now.getMonth()-1, 1)
            const end   = new Date(now.getFullYear(), now.getMonth(), 0)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: 'This Year',
          onClick(picker) {
            const year  = new Date().getFullYear()
            const start = new Date(year, 0, 1)
            const end   = new Date(year + 1, 0, 0)
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: 'Last Year',
          onClick(picker) {
            const now  = new Date()
            const yr   = now.getFullYear() - 1
            const start = new Date(yr, 0, 1)
            const end   = new Date(yr, 11, 31)
            picker.$emit('pick', [start, end])
          }
        }
      ]
    }
  },

  watch: {
    start(newStart) {
      this._syncRange(newStart, this.end)
    },
    end(newEnd) {
      this._syncRange(this.start, newEnd)
    }
  },

  computed: {
    ...mapGetters('cache', ['getCacheBucket']),

    hasError() {
      return this.validation && this.validation.$error;
    },
  },

  methods: {
    ...mapActions('cache', ['setBucket', 'clearBucket']),

    _syncRange(s, e) {
      if (s && e) {
        const startDate = new Date(s.split('T')[0])
        let endDate = new Date(e.split('T')[0])
        this.range = [startDate, endDate]
      } else {
        this.range = []
      }
    },

    onRangeChange(selection) {
      if (Array.isArray(selection) && selection.length === 2) {
        const [startDate, endDate] = selection
        const fmt = d => {
          const Y = d.getFullYear()
          const M = String(d.getMonth()+1).padStart(2,'0')
          const D = String(d.getDate()).padStart(2,'0')
          return `${Y}-${M}-${D}`
        }
        const startStr = fmt(startDate), endStr = fmt(endDate)

        this.$emit('update:start', startStr)
        this.$emit('update:end',   endStr)

        if (this.cache && this.cacheId) {
          this.setBucket({
            bucket: this.cacheId,
            data: { start: startStr, end: endStr }
          })
        }
      } else {
        this.$emit('update:start', null)
        this.$emit('update:end',   null)

        if (this.cache && this.cacheId) {
          this.clearBucket(this.cacheId)
        }
      }
    }
  },

  mounted() {
    if (this.cache && this.cacheId) {
      const bucket = this.getCacheBucket(this.cacheId)
      if (bucket?.data?.start && bucket.data.end) {

        const shouldUseCache = !this.rememberToday || (() => {
          const d = new Date(bucket.cached_at)
          return d.toDateString() === new Date().toDateString()
        })()

        if (shouldUseCache) {
          const { start, end } = bucket.data
          this._syncRange(start, end)
          this.$emit('update:start', start)
          this.$emit('update:end',   end)
          return
        }
      }
    }
    if (this.default) {
      const preset = this.shortcuts.find(shortcut => shortcut.text === this.default)
      if (preset) {
        preset.onClick({
          $emit: (_evt,val) => this.onRangeChange(val)
        })
      }
    }
  }
}
</script>

<style scoped>
.el-input__inner {
  border-radius: 6px;
  border: 1px solid rgb(206, 212, 218);
  height: 36px !important;
  line-height: 36px;
  padding: 0 12px;
  position: relative;
  box-sizing: border-box;
}

.my-date-picker ::v-deep .el-input__prefix,
.my-date-picker ::v-deep .el-input__suffix {
  position: absolute !important;
  top: 50%              !important;
  transform: translateY(-50%) !important;
  font-size: 16px       !important;
  color: #999           !important;
}

.my-date-picker ::v-deep .el-range-separator {
  line-height: 36px;
  margin: 0 8px;
}

.my-date-picker ::v-deep input.el-range-input {
  font-size: 13px !important;
}

.el-input__inner.dateInvalid,
.my-date-picker.dateInvalid ::v-deep input.el-range-input {
  border-color: var(--bs-danger) !important;
  color: var(--bs-danger);
  background-color: rgba(255,0,0,0.2) !important;
}

.my-date-picker.dateInvalid ::v-deep input.el-range-input {
  background-color: rgba(255,0,0,0.0) !important;
}

.el-input__inner.dateWarning,
.my-date-picker.dateWarning ::v-deep input.el-range-input {
  border-color: #fc9f3f !important;
  color: #fc9f3f;
  background-color: rgba(252,159,63,0.2) !important;
}

.my-date-picker.dateWarning ::v-deep input.el-range-input {
  background-color: rgba(252,159,63,0.0) !important;
}
</style>